import { Badge, Box, Button, Group, Stack, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { DataTableColumn } from 'mantine-datatable';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ApiError } from 'src/error/ApiError';
import { OnboardingInvite, OnboardingInviteStatus } from 'src/api/onboardingInvites';
import { useCreateOnboardingInvite } from './hooks/useCreateOnboardingInvite';

const EMAIL_REGEX =
  /^[^\s@]+@([a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;

const statusBadgeColour: Record<OnboardingInviteStatus, string> = {
  PENDING: 'orange',
  COMPLETED: 'green',
};

const statusLabel: Record<OnboardingInviteStatus, string> = {
  PENDING: 'Pending',
  COMPLETED: 'Completed',
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function SendToOnboardingPage() {
  const { createInvite, isLoading: isSending } = useCreateOnboardingInvite();

  const form = useForm({
    initialValues: { email: '' },
    validate: {
      email: value => (EMAIL_REGEX.test(value.trim()) ? null : 'Enter a valid email address'),
    },
  });

  const handleSend = async (values: typeof form.values) => {
    const email = values.email.trim();
    try {
      await createInvite({ email });
      notifications.show({
        title: 'Invite sent',
        message: `Onboarding invite sent to ${email}.`,
        color: 'green',
      });
      form.reset();
    } catch (error) {
      const errorMessage =
        error instanceof ApiError ? error.message : 'An unexpected error occurred.';
      notifyAndLogError('Failed to send invite', errorMessage, error);
    }
  };

  const columns: DataTableColumn<OnboardingInvite>[] = [
    { accessor: 'email', title: 'Email address' },
    {
      accessor: 'status',
      title: 'Status',
      render: row => (
        <Badge color={statusBadgeColour[row.status]} variant="light">
          {statusLabel[row.status]}
        </Badge>
      ),
    },
    { accessor: 'createdAt', title: 'Created', render: row => formatDateTime(row.createdAt) },
    { accessor: 'sentAt', title: 'Sent', render: row => formatDateTime(row.sentAt) },
    { accessor: 'expiresAt', title: 'Expires', render: row => formatDateTime(row.expiresAt) },
  ];

  return (
    <Box p="md">
      <Stack gap="lg">
        <Title order={2}>Send to onboarding</Title>

        <form onSubmit={form.onSubmit(handleSend)}>
          <Group align="flex-end">
            <TextInput
              label="Email address"
              placeholder="prospect@example.com"
              style={{ flex: 1 }}
              key={form.key('email')}
              disabled={isSending}
              {...form.getInputProps('email')}
            />
            <Button type="submit" loading={isSending}>
              Send invite
            </Button>
          </Group>
        </form>

        <SimpleTable<OnboardingInvite>
          resource="admin/onboarding-invites"
          columns={columns}
          title="Invites sent"
          showCount
          enableSearch
          searchAttributes={['email']}
          pageSize={20}
        />
      </Stack>
    </Box>
  );
}
